import React from 'react';
import {
    COMMON_TARGET_LABELS,
    DIRECTION_OPTIONS,
    TARGET_TYPE_OPTIONS,
    buildPresentationDefaults,
    getPresetOptions,
    normaliseTargetLabels,
    parseTargetLabelsInput
} from './standClayConfig';

const StandClayConfigFields = ({
    discipline,
    targetLabels,
    presentationDefaults,
    customTargetInput,
    onCustomTargetInputChange,
    onTargetLabelsChange,
    onPresentationDefaultsChange
}) => {
    const safeTargetLabels = normaliseTargetLabels(targetLabels);
    const safePresentationDefaults = buildPresentationDefaults(safeTargetLabels, presentationDefaults);
    const presets = getPresetOptions(discipline);

    const toggleLabel = (label) => {
        const nextLabels = safeTargetLabels.includes(label)
            ? safeTargetLabels.filter((item) => item !== label)
            : [...safeTargetLabels, label];

        onTargetLabelsChange(normaliseTargetLabels(nextLabels));
        onPresentationDefaultsChange(buildPresentationDefaults(nextLabels, safePresentationDefaults));
    };

    const addCustomLabels = () => {
        const customLabels = parseTargetLabelsInput(customTargetInput);
        if (customLabels.length === 0) {
            return;
        }

        const nextLabels = normaliseTargetLabels([...safeTargetLabels, ...customLabels]);
        onTargetLabelsChange(nextLabels);
        onPresentationDefaultsChange(buildPresentationDefaults(nextLabels, safePresentationDefaults));
        onCustomTargetInputChange('');
    };

    const handlePresetSelect = (labels) => {
        onTargetLabelsChange(labels);
        onPresentationDefaultsChange(buildPresentationDefaults(labels, safePresentationDefaults));
    };

    const updatePresentationDefault = (label, field, value) => {
        onPresentationDefaultsChange({
            ...safePresentationDefaults,
            [label]: {
                ...safePresentationDefaults[label],
                [field]: value
            }
        });
    };

    return (
        <div className="form-group col-span-2">
            <label>Clay Layout</label>
            <div className="stand-setup-panel">
                <p className="stand-setup-copy">
                    Choose the clay labels shooters should see when this stand is launched from a saved setup. One label can be a single bird, or you can set up fuller layouts with two, three, five, or more targets.
                </p>

                <div className="stand-setup-section">
                    <span className="stand-setup-label">Quick presets</span>
                    <div className="stand-setup-chip-row">
                        {presets.map((preset) => {
                            const isActive = JSON.stringify(safeTargetLabels) === JSON.stringify(preset.labels);
                            return (
                                <button
                                key={preset.label}
                                type="button"
                                className={`stand-setup-chip ${isActive ? 'active' : ''}`}
                                onClick={() => handlePresetSelect(preset.labels)}
                            >
                                {preset.label}
                            </button>
                            );
                        })}
                    </div>
                </div>

                <div className="stand-setup-section">
                    <span className="stand-setup-label">Common labels</span>
                    <div className="stand-setup-chip-row">
                        {COMMON_TARGET_LABELS.map((label) => (
                            <button
                                key={label}
                                type="button"
                                className={`stand-setup-chip ${safeTargetLabels.includes(label) ? 'active' : ''}`}
                                onClick={() => toggleLabel(label)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="stand-setup-section">
                    <span className="stand-setup-label">Custom labels</span>
                    <div className="stand-setup-custom-row">
                        <input
                            type="text"
                            value={customTargetInput}
                            onChange={(event) => onCustomTargetInputChange(event.target.value)}
                            placeholder="AB, Midi, Rabbit pair"
                        />
                        <button
                            type="button"
                            className="dashboard-card-outline-btn"
                            onClick={addCustomLabels}
                            disabled={!customTargetInput.trim()}
                        >
                            Add label
                        </button>
                    </div>
                    <p className="stand-setup-helper">Separate custom labels with commas if you want to add more than one at once.</p>
                </div>

                <div className="stand-setup-section">
                    <p className="stand-setup-helper">
                        Whatever you select here flows into quick fun shoot. If this stand throws one clay, pick one label. If it throws a full five-bird layout, pick five.
                    </p>
                </div>

                <div className="stand-setup-section">
                    <span className="stand-setup-label">Selected labels</span>
                    <div className="stand-setup-chip-row">
                        {safeTargetLabels.length > 0 ? safeTargetLabels.map((label) => (
                            <button
                                key={label}
                                type="button"
                                className="stand-setup-chip active"
                                onClick={() => toggleLabel(label)}
                                title="Remove label"
                            >
                                {label}
                            </button>
                        )) : <span className="stand-setup-empty">No clay labels selected yet.</span>}
                    </div>
                </div>

                {safeTargetLabels.length > 0 && (
                    <div className="stand-setup-section">
                        <span className="stand-setup-label">Default presentation details</span>
                        <p className="stand-setup-helper">
                            These defaults flow straight into quick fun shoots so shooters are not asked to re-enter direction and distance every time.
                        </p>
                        <div className="stand-setup-presentation-list">
                            {safeTargetLabels.map((label) => (
                                <div key={label} className="stand-setup-presentation-card">
                                    <div className="stand-setup-presentation-header">
                                        <strong>{label}</strong>
                                        <span>Default presentation</span>
                                    </div>
                                    <div className="stand-setup-presentation-grid">
                                        <label>
                                            Type
                                            <select
                                                value={safePresentationDefaults[label]?.targetType || 'single'}
                                                onChange={(event) => updatePresentationDefault(label, 'targetType', event.target.value)}
                                            >
                                                {TARGET_TYPE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Direction
                                            <select
                                                value={safePresentationDefaults[label]?.direction || ''}
                                                onChange={(event) => updatePresentationDefault(label, 'direction', event.target.value)}
                                            >
                                                {DIRECTION_OPTIONS.map((option) => (
                                                    <option key={option.value || 'blank'} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            Distance (yards)
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                value={safePresentationDefaults[label]?.distance || ''}
                                                onChange={(event) => updatePresentationDefault(label, 'distance', event.target.value)}
                                                placeholder="Optional"
                                            />
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StandClayConfigFields;
